import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDbUser } from '../hooks/useDbUser';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { authApiClient } from '../services/authApi';
import { SavedItemsPanel, NotificationsPanel, type SavedItemsData, type NotificationsData } from '../components/admin/ActivityPanels';
import { SubscriptionsTab } from '../components/admin/SubscriptionsTab';
import { UnresolvedGenericsTab } from '../components/admin/UnresolvedGenericsTab';
import { SalesToneFixerTab } from '../components/admin/SalesToneFixerTab';
import {
  CronASummaryTable, VERSIONS_NA_TOOLTIP, cronAColumnTooltips, cronASplit, familyLabel,
  type CronASplit,
} from '../components/admin/CronASummaryTable';
import { CRON_B_STAGES, CronBStageTable, type CronBStage } from '../components/admin/CronBStageTable';
import { CronBSummaryTable } from '../components/admin/CronBSummaryTable';
import { CorpusVerificationPanel } from '../components/admin/CorpusVerificationPanel';
import {
  formatLatency,
  formatToolNames,
  formatTotalTokens,
} from '../utils/cepiTraceFormatters';

// Types
interface AuditRun {
  id: number;
  run_id: string;
  started_at: string | null;
  completed_at: string | null;
  phases_run: string;
  status: string;
  region: string;
  total_flagged: number;
  total_mismatched: number;
  total_reingested: number;
  verdicts: Record<string, number> | null;
  anomaly_reasons: Record<string, number> | null;
  error_message: string | null;
  flagged_drugs?: Record<string, any> | null;
}

interface SelfHealRunRow {
  id: number;
  run_id: string;
  started_at: string | null;
  completed_at: string | null;
  status: string;
  mode: string;
  discovery_source: string | null;
  discovery_snapshot_count: number | null;
  applied_catalog_count: number | null;
  appnum: {
    filled: number;
    skipped_multi_app: number;
    skipped_no_app: number;
    drugs_touched: number;
  } | null;
  meta_scanned: number | null;
  meta_changed: number | null;
  meta_preserved_trusted_fk: number | null;
  reason: string | null;
}

interface ReconciliationRunRow {
  id: number;
  run_id: string;
  started_at: string | null;
  completed_at: string | null;
  status: string;
  mode: string;
  environment: string | null;
  job_kind: string | null;
  routes_attempted: number | null;
  total_selected: number | null;
  total_promoted: number | null;
  total_held: number | null;
  total_equivalent: number | null;
  total_failed: number | null;
  per_route: any;
  reason: string | null;
}

interface DbReport {
  database_size: string;
  tables: { table: string; size: string; size_bytes: number }[];
  row_counts: Record<string, number | null>;
  drugs_by_region: { region: string; count: number }[];
}

interface UserActivity {
  email: string;
  call_count: number;
  request_count: number;
  total_cost: number;
  last_activity: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
}

interface VDIntegrityAlert {
  severity: string;
  counter: string;
  message: string;
}

interface VDIntegrityReport {
  date: string | null;
  audited_at: string | null;
  counters: Record<string, number>;
  alerts: VDIntegrityAlert[];
  hard_alert_count: number;
  previous_day_uri: string | null;
  blob_name: string;
}

interface Deferral {
  id: number;
  source: string;
  source_identifier: string;
  reason: string;
  status: string;
  sba_document_id: number | null;
  trade_name: string | null;
  application_number: string | null;
  spent_usd: number | null;
  ocr_pages: number | null;
  pages_total: number | null;
  review_types: string[] | null;
  created_at: string | null;
}
interface DeferralSummary {
  reason: string;
  status: string;
  count: number;
  spent_usd: number;
}

type Tab = 'audits' | 'selfheal' | 'reconciliation' | 'vd-integrity' | 'database' | 'activity' | 'costs' | 'deferrals' | 'cache' | 'impersonate' | 'subscriptions' | 'generics-unresolved' | 'sales-tone';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  // DB-backed admin gate (Task 7) — replaces the old frontend ALLOWED_EMAILS
  // check. `adminLoading` (renamed from useDbUser's `loading` to avoid
  // colliding with the tab-data-fetch `loading` state below) must resolve
  // to false before we redirect a non-admin away, else a real admin briefly
  // flashes a redirect to "/" on every load while POST /auth/sync is
  // in-flight.
  const { isAdmin, loading: adminLoading } = useDbUser();

  // A shared ?cron=…&stage=…&view=… link opens Data Health directly.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(searchParams.get('cron') ? 'reconciliation' : 'audits');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Audit state
  const [auditRuns, setAuditRuns] = useState<AuditRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<AuditRun | null>(null);

  // Self-heal state
  const [selfHealRuns, setSelfHealRuns] = useState<SelfHealRunRow[]>([]);
  const [reconciliationRuns, setReconciliationRuns] = useState<ReconciliationRunRow[]>([]);
  // When the merge job last copied Cron A rows from the backfill buffer into this DB.
  const [reconLastSyncedAt, setReconLastSyncedAt] = useState<string | null>(null);

  // VD integrity state
  const [vdReports, setVdReports] = useState<VDIntegrityReport[]>([]);
  const [vdBucket, setVdBucket] = useState<string | null>(null);
  const [vdError, setVdError] = useState<string | null>(null);

  // DB report state
  const [dbReport, setDbReport] = useState<DbReport | null>(null);

  // Activity state
  const [userActivity, setUserActivity] = useState<UserActivity[]>([]);
  const [recentCalls, setRecentCalls] = useState<any[]>([]);
  const [activityDays, setActivityDays] = useState(7);
  const [filterEmail, setFilterEmail] = useState('');
  const summaryGen = useRef(0);
  const callsGen = useRef(0);
  const [savedItems, setSavedItems] = useState<SavedItemsData | null>(null);
  const [savedItemsError, setSavedItemsError] = useState(false);
  const savedGen = useRef(0);
  const [notifications, setNotifications] = useState<NotificationsData | null>(null);
  const [notificationsError, setNotificationsError] = useState(false);
  const notifGen = useRef(0);
  const [activityOrg, setActivityOrg] = useState(''); // '' = all, 'none', or org id
  const [orgOptions, setOrgOptions] = useState<{ id: number; name: string }[]>([]);

  // LLM costs state
  const [costSummary, setCostSummary] = useState<any>(null);
  const [costDaily, setCostDaily] = useState<any[]>([]);
  const [costDays, setCostDays] = useState(7);
  const [costGroupBy, setCostGroupBy] = useState<'user' | 'pipeline' | 'model' | 'stage'>('user');
  const [reconciliation, setReconciliation] = useState<any>(null);
  const [reconBucket, setReconBucket] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [costUserFilter, setCostUserFilter] = useState('');

  // Deferred ingestions (Gemini OCR cost gate)
  const [deferrals, setDeferrals] = useState<Deferral[]>([]);
  const [deferralSummary, setDeferralSummary] = useState<DeferralSummary[]>([]);
  const [deferralReason, setDeferralReason] = useState<string>('');

  useEffect(() => {
    // Don't redirect until the DB flag has actually resolved — while
    // adminLoading is true, isAdmin is still its initial `false`, and a
    // real admin would otherwise get bounced to "/" for a moment on every
    // page load before the sync response lands.
    if (!adminLoading && !isAdmin) {
      navigate('/');
    }
  }, [isAdmin, adminLoading, navigate]);

  useEffect(() => {
    if (activeTab === 'audits') fetchAuditRuns();
    if (activeTab === 'selfheal') fetchSelfHealRuns();
    if (activeTab === 'reconciliation') fetchReconciliationRuns();
    if (activeTab === 'vd-integrity') fetchVdIntegrity();
    if (activeTab === 'database') fetchDbReport();
    if (activeTab === 'costs') {
      fetchCostSummary();
      fetchCostDaily();
      fetchReconciliation();
    }
    if (activeTab === 'deferrals') fetchDeferrals();
  }, [activeTab, costDays, costGroupBy, reconBucket, costUserFilter, deferralReason]);

  useEffect(() => {
    if (activeTab === 'activity') fetchActivitySummary();
  }, [activeTab, activityDays, activityOrg]);

  // Recent calls refetch whenever any filter changes (a single Filter click
  // must apply immediately; no setTimeout against a stale closure).
  useEffect(() => {
    if (activeTab === 'activity') fetchRecentCalls();
  }, [activeTab, activityDays, filterEmail, activityOrg]);

  useEffect(() => {
    if (activeTab === 'activity') fetchSavedItems();
  }, [activeTab, filterEmail, activityOrg]);

  useEffect(() => {
    if (activeTab === 'activity') fetchNotifications();
  }, [activeTab, activityDays, filterEmail, activityOrg]);

  useEffect(() => {
    if (activeTab !== 'activity' || orgOptions.length > 0) return;
    authApiClient.get('/admin/organizations')
      .then(res => setOrgOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => { /* non-critical: tenant dropdown just has no orgs */ });
  }, [activeTab]);

  // Live poll of the Cron A board every 10s while it's shown, so in-progress runs
  // fill in per-family without a manual refresh. Cron B polls its own endpoint
  // (CronBStageTable), so this one stops while Cron B is open.
  const showingCronB = searchParams.get('cron') === 'B';
  useEffect(() => {
    if (activeTab !== 'reconciliation' || showingCronB) return;
    const id = setInterval(() => fetchReconciliationRuns(true), 10000);
    return () => clearInterval(id);
  }, [activeTab, showingCronB]);

  // API calls
  const fetchAuditRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authApiClient.get('/admin/audit-runs?limit=20');
      setAuditRuns(res.data.runs);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch audit runs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSelfHealRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authApiClient.get('/admin/selfheal-runs?limit=50');
      setSelfHealRuns(res.data.runs);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch self-heal runs');
    } finally {
      setLoading(false);
    }
  };

  const fetchReconciliationRuns = async (silent = false) => {
    // silent=true is used by the live-poll: no spinner, no error banner on a
    // transient failure (the next tick just retries).
    if (!silent) { setLoading(true); setError(null); }
    try {
      // Cron A rows only: Cron B's recon_* rows must not push families out of the window.
      const res = await authApiClient.get('/admin/reconciliation-runs?limit=50&job_kind=discover');
      setReconciliationRuns(res.data.runs);
      setReconLastSyncedAt(res.data.last_synced_at ?? null);
    } catch (e: any) {
      if (!silent) setError(e.response?.data?.detail || 'Failed to fetch reconciliation runs');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchAuditRunDetail = async (id: number) => {
    setLoading(true);
    try {
      const res = await authApiClient.get(`/admin/audit-runs/${id}`);
      setSelectedRun(res.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch audit run');
    } finally {
      setLoading(false);
    }
  };

  const fetchVdIntegrity = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authApiClient.get('/admin/version-diffs-integrity?limit=14');
      setVdReports(res.data.reports || []);
      setVdBucket(res.data.bucket || null);
      setVdError(res.data.error || null);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch VD integrity reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchDbReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authApiClient.get('/admin/db-report');
      setDbReport(res.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch DB report');
    } finally {
      setLoading(false);
    }
  };

  const fetchActivitySummary = async () => {
    const gen = ++summaryGen.current; // only the latest request may update state
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ days: String(activityDays) });
      if (activityOrg) params.set('organization', activityOrg);
      const res = await authApiClient.get(`/admin/user-activity/summary?${params}`);
      if (gen === summaryGen.current) setUserActivity(res.data.users);
    } catch (e: any) {
      if (gen === summaryGen.current) setError(e.response?.data?.detail || 'Failed to fetch activity');
    } finally {
      if (gen === summaryGen.current) setLoading(false);
    }
  };

  const fetchRecentCalls = async () => {
    const gen = ++callsGen.current;
    try {
      const params = new URLSearchParams({ days: String(activityDays), limit: '100' });
      if (filterEmail) params.set('user_email', filterEmail);
      if (activityOrg) params.set('organization', activityOrg);
      const res = await authApiClient.get(`/admin/user-activity?${params}`);
      if (gen === callsGen.current) setRecentCalls(res.data.actions || []);
    } catch {
      // non-critical
    }
  };

  const fetchSavedItems = async () => {
    const gen = ++savedGen.current;
    try {
      const params = new URLSearchParams();
      if (filterEmail) params.set('user_email', filterEmail);
      if (activityOrg) params.set('organization', activityOrg);
      const res = await authApiClient.get(`/admin/user-activity/saved-items?${params}`);
      if (gen === savedGen.current) {
        setSavedItems({
          subscriptions: res.data.subscriptions || [],
          starred_lists: res.data.starred_lists || [],
        });
        setSavedItemsError(false);
      }
    } catch {
      if (gen === savedGen.current) setSavedItemsError(true);
    }
  };

  const fetchNotifications = async () => {
    const gen = ++notifGen.current;
    try {
      const params = new URLSearchParams({ days: String(activityDays) });
      if (filterEmail) params.set('user_email', filterEmail);
      if (activityOrg) params.set('organization', activityOrg);
      const res = await authApiClient.get(`/admin/user-activity/notifications?${params}`);
      if (gen === notifGen.current) {
        setNotifications({
          alerts: res.data.alerts || [],
          research_emails: res.data.research_emails || [],
        });
        setNotificationsError(false);
      }
    } catch {
      if (gen === notifGen.current) setNotificationsError(true);
    }
  };

  const fetchCostSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ days: String(costDays), group_by: costGroupBy });
      if (costUserFilter) params.set('user_email', costUserFilter);
      const res = await authApiClient.get(`/admin/llm-costs/summary?${params}`);
      setCostSummary(res.data);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch costs');
    } finally {
      setLoading(false);
    }
  };

  const fetchCostDaily = async () => {
    try {
      const res = await authApiClient.get(`/admin/llm-costs/daily?days=${costDays}`);
      setCostDaily(res.data.daily || []);
    } catch {
      // non-critical
    }
  };

  const fetchReconciliation = async () => {
    try {
      const res = await authApiClient.get(`/admin/llm-costs/reconciliation?days=${costDays}&bucket=${reconBucket}`);
      setReconciliation(res.data);
    } catch {
      // non-critical
    }
  };

  const fetchDeferrals = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ status: 'deferred', limit: '200' });
      if (deferralReason) params.set('reason', deferralReason);
      const res = await authApiClient.get(`/admin/deferred-ingestions?${params}`);
      setDeferrals(res.data.deferrals || []);
      setDeferralSummary(res.data.summary || []);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to fetch deferred ingestions');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      completed: 'bg-green-100 text-green-700',
      running: 'bg-blue-100 text-blue-700',
      failed: 'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
        {status}
      </span>
    );
  };

  // Also covers the adminLoading window: isAdmin's initial value is false,
  // so this stays blank (not a flash of dashboard content, not a redirect)
  // until useDbUser resolves.
  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Admin Dashboard</h1>
            <p className="text-sm text-slate-500">System monitoring & data freshness audits</p>
          </div>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Back to Search
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-6 pt-4">
        <div className="flex space-x-1 bg-slate-200 rounded-lg p-1 w-fit">
          {(['audits', 'selfheal', 'reconciliation', 'vd-integrity', 'database', 'activity', 'costs', 'deferrals', 'cache', 'impersonate', 'subscriptions', 'generics-unresolved', 'sales-tone'] as Tab[]).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {tab === 'audits' ? 'Audit Runs'
                : tab === 'selfheal' ? 'Self-heal'
                : tab === 'reconciliation' ? 'Data Health'
                : tab === 'vd-integrity' ? 'VD Integrity'
                : tab === 'database' ? 'Database'
                : tab === 'activity' ? 'User Activity'
                : tab === 'costs' ? 'LLM Costs'
                : tab === 'deferrals' ? 'Deferrals'
                : tab === 'cache' ? 'Cache'
                : tab === 'subscriptions' ? 'Subscriptions'
                : tab === 'generics-unresolved' ? 'Unresolved generics'
                : tab === 'sales-tone' ? 'Sales Tone Fixer'
                : 'Impersonate'}
            </button>
          ))}
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading && (
          <div className="mt-4 text-sm text-slate-500">Loading...</div>
        )}

        {/* Tab Content */}
        <div className="mt-4 pb-8">
          {activeTab === 'audits' && !selectedRun && (
            <AuditRunsTab
              runs={auditRuns}
              onSelect={(id) => fetchAuditRunDetail(id)}
              formatDate={formatDate}
              statusBadge={statusBadge}
            />
          )}

          {activeTab === 'audits' && selectedRun && (
            <AuditRunDetail
              run={selectedRun}
              onBack={() => setSelectedRun(null)}
              formatDate={formatDate}
              statusBadge={statusBadge}
            />
          )}

          {activeTab === 'selfheal' && (
            <SelfHealRunsTab
              runs={selfHealRuns}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'reconciliation' && (
            <ReconciliationRunsTab
              runs={reconciliationRuns}
              lastSyncedAt={reconLastSyncedAt}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'vd-integrity' && (
            <VDIntegrityTab
              reports={vdReports}
              bucket={vdBucket}
              fetchError={vdError}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'database' && dbReport && (
            <DatabaseTab report={dbReport} />
          )}

          {activeTab === 'activity' && (
            <ActivityTab
              users={userActivity}
              calls={recentCalls}
              savedItems={savedItems}
              savedItemsError={savedItemsError}
              notifications={notifications}
              notificationsError={notificationsError}
              days={activityDays}
              onDaysChange={(d) => { setActivityDays(d); }}
              filterEmail={filterEmail}
              onFilterEmail={setFilterEmail}
              orgOptions={orgOptions}
              organization={activityOrg}
              onOrganizationChange={setActivityOrg}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'costs' && (
            <CostsTab
              summary={costSummary}
              daily={costDaily}
              days={costDays}
              onDaysChange={setCostDays}
              groupBy={costGroupBy}
              onGroupByChange={setCostGroupBy}
              reconciliation={reconciliation}
              reconBucket={reconBucket}
              onReconBucketChange={setReconBucket}
              userFilter={costUserFilter}
              onUserFilter={(e) => { setCostUserFilter(e); }}
            />
          )}

          {activeTab === 'deferrals' && (
            <DeferralsTab
              deferrals={deferrals}
              summary={deferralSummary}
              reason={deferralReason}
              onReasonChange={setDeferralReason}
              formatDate={formatDate}
            />
          )}

          {activeTab === 'cache' && <CacheTab />}

          {activeTab === 'impersonate' && <ImpersonateTab />}

          {activeTab === 'subscriptions' && <SubscriptionsTab />}

          {activeTab === 'generics-unresolved' && <UnresolvedGenericsTab />}

          {activeTab === 'sales-tone' && <SalesToneFixerTab />}
        </div>
      </div>
    </div>
  );
};

// =========================================================================
// Sub-components
// =========================================================================

const DeferralsTab: React.FC<{
  deferrals: Deferral[];
  summary: DeferralSummary[];
  reason: string;
  onReasonChange: (r: string) => void;
  formatDate: (d: string | null) => string;
}> = ({ deferrals, summary, reason, onReasonChange, formatDate }) => {
  const totalSpent = summary
    .filter(s => s.reason === 'cost_capped')
    .reduce((a, s) => a + (s.spent_usd || 0), 0);
  const openCount = summary
    .filter(s => s.status === 'deferred')
    .reduce((a, s) => a + s.count, 0);
  const reasonBadge = (r: string) => (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
      r === 'cost_capped' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
    }`}>{r}</span>
  );
  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-xs text-slate-500">Open deferrals</div>
          <div className="text-2xl font-semibold text-slate-900">{openCount.toLocaleString()}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-xs text-slate-500">Deferred Gemini spend (capped)</div>
          <div className="text-2xl font-semibold text-slate-900">${totalSpent.toFixed(2)}</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-xs text-slate-500">By reason / status</div>
          <div className="text-xs text-slate-600 mt-1 space-y-0.5">
            {summary.length === 0 && <span className="text-slate-400">—</span>}
            {summary.map(s => (
              <div key={`${s.reason}-${s.status}`}>
                {s.reason} · {s.status}: <span className="font-mono">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-slate-500">Reason:</span>
        <select
          value={reason}
          onChange={e => onReasonChange(e.target.value)}
          className="border border-slate-300 rounded px-2 py-1 text-sm bg-white"
        >
          <option value="">All</option>
          <option value="cost_capped">cost_capped</option>
          <option value="ocr_page_ceiling">ocr_page_ceiling</option>
          <option value="embedding_failed">embedding_failed</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Created</th>
              <th className="text-left px-4 py-3 font-medium">Reason</th>
              <th className="text-left px-4 py-3 font-medium">Drug / App</th>
              <th className="text-right px-4 py-3 font-medium">Spent</th>
              <th className="text-right px-4 py-3 font-medium">OCR pages</th>
              <th className="text-left px-4 py-3 font-medium">Doc</th>
              <th className="text-left px-4 py-3 font-medium">Reprocess key</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {deferrals.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-400">
                  No deferred ingestions. The cost gate writes a row here when it
                  caps or skips a doc (INGESTION_COST_GATE_MODE=enforce).
                </td>
              </tr>
            )}
            {deferrals.map(d => (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-700">{formatDate(d.created_at)}</td>
                <td className="px-4 py-3">{reasonBadge(d.reason)}</td>
                <td className="px-4 py-3 text-slate-700">
                  {d.trade_name || '—'}
                  <span className="text-slate-400 font-mono text-xs">
                    {d.application_number ? ` #${d.application_number}` : ''}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {d.spent_usd != null ? `$${d.spent_usd.toFixed(4)}` : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {d.ocr_pages != null ? `${d.ocr_pages}${d.pages_total ? ` / ${d.pages_total}` : ''}` : '—'}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{d.sba_document_id ?? '—'}</td>
                <td
                  className="px-4 py-3 font-mono text-xs text-slate-500 truncate max-w-xs"
                  title={d.source_identifier}
                >
                  {d.source_identifier}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const AuditRunsTab: React.FC<{
  runs: AuditRun[];
  onSelect: (id: number) => void;
  formatDate: (d: string | null) => string;
  statusBadge: (s: string) => JSX.Element;
}> = ({ runs, onSelect, formatDate, statusBadge }) => (
  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="text-left px-4 py-3 font-medium">Started</th>
          <th className="text-left px-4 py-3 font-medium">Region</th>
          <th className="text-left px-4 py-3 font-medium">Phases</th>
          <th className="text-left px-4 py-3 font-medium">Status</th>
          <th className="text-right px-4 py-3 font-medium">Flagged</th>
          <th className="text-right px-4 py-3 font-medium">Mismatched</th>
          <th className="text-right px-4 py-3 font-medium">Re-ingested</th>
          <th className="text-left px-4 py-3 font-medium">Duration</th>
          <th className="px-4 py-3"></th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {runs.length === 0 && (
          <tr>
            <td colSpan={9} className="px-4 py-8 text-center text-slate-400">
              No audit runs yet. The nightly cron runs at 05:00 UTC daily.
            </td>
          </tr>
        )}
        {runs.map(run => {
          const duration = run.started_at && run.completed_at
            ? Math.round((new Date(run.completed_at).getTime() - new Date(run.started_at).getTime()) / 60000)
            : null;
          return (
            <tr key={run.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onSelect(run.id)}>
              <td className="px-4 py-3 text-slate-700">{formatDate(run.started_at)}</td>
              <td className="px-4 py-3 font-mono text-xs">{run.region}</td>
              <td className="px-4 py-3 font-mono text-xs">{run.phases_run}</td>
              <td className="px-4 py-3">{statusBadge(run.status)}</td>
              <td className="px-4 py-3 text-right font-mono">{run.total_flagged?.toLocaleString()}</td>
              <td className="px-4 py-3 text-right font-mono">{run.total_mismatched?.toLocaleString()}</td>
              <td className="px-4 py-3 text-right font-mono">{run.total_reingested?.toLocaleString()}</td>
              <td className="px-4 py-3 text-slate-500">{duration != null ? `${duration}m` : '—'}</td>
              <td className="px-4 py-3 text-indigo-600 text-xs">View</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// Status colors for selfheal_runs.status ("ok" / "aborted" / "failed") --
// distinct from the audit-run `statusBadge` above, which is keyed to
// "completed" / "running" / "failed".
const selfHealStatusBadge = (status: string) => {
  const colors: Record<string, string> = {
    ok: 'bg-green-100 text-green-700',
    aborted: 'bg-red-100 text-red-700',
    failed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

const SELF_HEAL_COL_COUNT = 8;

// Flat table, no drill-down (each row is already the full run — unlike
// AuditRunsTab, self-heal has no per-run detail payload to expand into).
const SelfHealRunsTab: React.FC<{
  runs: SelfHealRunRow[];
  formatDate: (d: string | null) => string;
}> = ({ runs, formatDate }) => (
  <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="text-left px-4 py-3 font-medium">Started</th>
          <th className="text-left px-4 py-3 font-medium">Status</th>
          <th className="text-left px-4 py-3 font-medium">Mode</th>
          <th className="text-left px-4 py-3 font-medium">Discovery source</th>
          <th className="text-right px-4 py-3 font-medium">Catalog count</th>
          <th className="text-right px-4 py-3 font-medium">Appnum filled</th>
          <th className="text-right px-4 py-3 font-medium">Meta changed</th>
          <th className="text-right px-4 py-3 font-medium">Meta FK preserved</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {runs.length === 0 && (
          <tr>
            <td colSpan={SELF_HEAL_COL_COUNT} className="px-4 py-8 text-center text-slate-400">
              No self-heal runs yet. The nightly cron runs at 16:00 UTC daily.
            </td>
          </tr>
        )}
        {runs.map(run => {
          const isBad = run.status === 'aborted' || run.status === 'failed';
          return (
            <React.Fragment key={run.id}>
              <tr className={`hover:bg-slate-50 ${isBad ? 'bg-red-50/50' : ''}`}>
                <td className="px-4 py-3 text-slate-700">{formatDate(run.started_at)}</td>
                <td className="px-4 py-3">{selfHealStatusBadge(run.status)}</td>
                <td className="px-4 py-3 font-mono text-xs">{run.mode}</td>
                <td className="px-4 py-3 font-mono text-xs">{run.discovery_source ?? '—'}</td>
                <td className="px-4 py-3 text-right font-mono">
                  {run.applied_catalog_count != null ? run.applied_catalog_count.toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {run.appnum?.filled != null ? run.appnum.filled.toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {run.meta_changed != null ? run.meta_changed.toLocaleString() : '—'}
                </td>
                <td className="px-4 py-3 text-right font-mono">
                  {run.meta_preserved_trusted_fk != null ? run.meta_preserved_trusted_fk.toLocaleString() : '—'}
                </td>
              </tr>
              {isBad && run.reason && (
                <tr>
                  <td colSpan={SELF_HEAL_COL_COUNT} className="px-4 pb-3">
                    <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm font-mono whitespace-pre-wrap">
                      {run.reason}
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  </div>
);

// Reconciliation nightly runs — one row per job (source/build never collapse).
const reconStatusBadge = (status: string) => {
  const colors: Record<string, string> = {
    ok: 'bg-green-100 text-green-700',
    running: 'bg-blue-100 text-blue-700',
    partial: 'bg-amber-100 text-amber-700',
    failed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-700'}`}>
      {status}
    </span>
  );
};

// Cron A and Cron B are two DIFFERENT jobs and report different things:
//   Cron A (job_kind='discover') — find NEW drugs → download → parse → ingest.
//     per_route [{family, region, new_discovered, created, failed, + version
//     counts (cronASplit)}]. Its Latest
//     view is built here from /admin/reconciliation-runs (latest row per family).
//   Cron B — the DB-store reconciliation pipeline (export → build → merge →
//     revert), one tab per job, served by /admin/reconcile/* and
//     /admin/cron-summary?cron=B (components/admin/CronBStageTable.tsx).
// Rows of the OLD Cron B (job_kind 'build' / 'source') are not shown anywhere
// (dashboard plan D5) so they never mix into the new numbers.
interface ReconJobRow {
  key: string;
  family: string;
  mode: string;
  status: string;
  startedAt: string | null;
  split: CronASplit;
  failed: number | null;
  raw: unknown;
}

interface SkipItem { set_id: string; version: string | number | null; kind: string; outcome: string; reason: string }
interface HeldItem {
  set_id: string; version: string | number | null; reason: string;
  first_skipped_at: string | null; retry_after: string | null; skip_count: number;
}

const dailyMedLink = (setId: string) => (
  <a
    href={`https://dailymed.nlm.nih.gov/dailymed/lookup.cfm?setid=${encodeURIComponent(setId)}`}
    target="_blank"
    rel="noopener noreferrer"
    className="text-indigo-600 hover:underline font-mono"
  >
    {setId}
  </a>
);

const flattenReconJobs = (runs: ReconciliationRunRow[]): ReconJobRow[] => {
  const latest = new Map<string, ReconJobRow>(); // runs are newest-first
  for (const run of runs) {
    if (run.job_kind !== 'discover') continue;
    const perRoute = Array.isArray(run.per_route) ? run.per_route : [];
    for (const e of perRoute) {
      const family = String(e.family ?? e.region ?? 'discover');
      const key = `A|${family}`;
      if (latest.has(key)) continue; // first seen == latest
      latest.set(key, {
        key,
        family,
        mode: run.mode,
        status: run.status,
        startedAt: run.started_at,
        split: cronASplit(e),
        failed: e.failed ?? null,
        raw: e,
      });
    }
  }
  return Array.from(latest.values()).sort((a, b) => a.family.localeCompare(b.family));
};

type CronBView = 'latest' | 'daily' | 'weekly';
const isCronBStage = (v: string | null): v is CronBStage => CRON_B_STAGES.some(st => st.key === v);
const isCronBView = (v: string | null): v is CronBView => v === 'latest' || v === 'daily' || v === 'weekly';

const ReconciliationRunsTab: React.FC<{
  runs: ReconciliationRunRow[];
  lastSyncedAt?: string | null;
  formatDate: (d: string | null) => string;
}> = ({ runs, lastSyncedAt, formatDate }) => {
  const [expanded, setExpanded] = React.useState<string | null>(null);
  // Cron tab, Cron B stage and Cron B view live in the URL so links can be shared.
  // Clicking Cron B with nothing in the URL opens Build → Latest (plan §3.6).
  const [params, setParams] = useSearchParams();
  const cronTab: 'A' | 'B' = params.get('cron') === 'B' ? 'B' : 'A';
  const rawStage = params.get('stage');
  const rawView = params.get('view');
  const cronBStage: CronBStage = isCronBStage(rawStage) ? rawStage : 'build';
  const cronBView: CronBView = isCronBView(rawView) ? rawView : 'latest';
  const setUrl = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v === null ? p.delete(k) : p.set(k, v)));
    setParams(p, { replace: true });
  };
  const setCronTab = (c: 'A' | 'B') =>
    setUrl(c === 'B' ? { cron: 'B', stage: cronBStage, view: cronBView } : { cron: 'A', stage: null, view: null });
  const [cronAView, setCronAView] =React.useState<'latest' | 'daily' | 'weekly' | 'verified'>('latest');
  const cronA = React.useMemo(() => flattenReconJobs(runs), [runs]);

  const isBad = (s: string) =>
    s === 'partial' || s === 'failed' || s === 'route_error' || s === 'timeout';

  const expandRow = (job: ReconJobRow, colSpan: number) => {
    const raw = (job.raw ?? {}) as {
      skipped_items?: unknown; skipped_items_total?: unknown;
      skip_memory_held_items?: unknown; skip_memory_held?: unknown;
    };
    const skipped = Array.isArray(raw.skipped_items) ? (raw.skipped_items as SkipItem[]) : [];
    const held = Array.isArray(raw.skip_memory_held_items) ? (raw.skip_memory_held_items as HeldItem[]) : [];
    const skippedTotal = typeof raw.skipped_items_total === 'number' ? raw.skipped_items_total : skipped.length;
    const heldCount = typeof raw.skip_memory_held === 'number' ? raw.skip_memory_held : held.length;
    const th = 'text-left px-3 py-2 font-medium';
    return expanded === job.key && (
      <tr>
        <td colSpan={colSpan} className="px-4 pb-3">
          {skipped.length > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">
                Not ingested this run ({skipped.length} of {skippedTotal})
              </h4>
              <table className="w-full text-xs bg-white border border-slate-200 rounded">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className={th}>Set ID</th><th className={th}>Version</th><th className={th}>Kind</th>
                    <th className={th}>Outcome</th><th className={th}>Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {skipped.map((it, i) => (
                    <tr key={`${it.set_id}-${i}`}>
                      <td className="px-3 py-2">{dailyMedLink(it.set_id)}</td>
                      <td className="px-3 py-2">{it.version}</td>
                      <td className="px-3 py-2">{it.kind}</td>
                      <td className="px-3 py-2">{it.outcome}</td>
                      <td className="px-3 py-2">{it.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {held.length > 0 && (
            <div className="mb-3">
              <h4 className="text-xs font-semibold text-slate-700 mb-1">Held by skip memory ({heldCount})</h4>
              <table className="w-full text-xs bg-white border border-slate-200 rounded">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className={th}>Set ID</th><th className={th}>Version</th><th className={th}>Reason</th>
                    <th className={th}>First skipped</th><th className={th}>Retry after</th><th className={th}>Count</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {held.map((it, i) => (
                    <tr key={`${it.set_id}-${i}`}>
                      <td className="px-3 py-2">{dailyMedLink(it.set_id)}</td>
                      <td className="px-3 py-2">{it.version}</td>
                      <td className="px-3 py-2">{it.reason}</td>
                      <td className="px-3 py-2">{formatDate(it.first_skipped_at)}</td>
                      <td className="px-3 py-2">{formatDate(it.retry_after)}</td>
                      <td className="px-3 py-2">{it.skip_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <pre className="p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(job.raw, null, 2)}
          </pre>
        </td>
      </tr>
    );
  };

  const numCell = (v: number | null, extra = '', title?: string) => (
    <td className={`px-4 py-3 text-right font-mono ${extra}`} title={title}>{v ?? '—'}</td>
  );
  const cronATips = cronAColumnTooltips('run');
  const versionCell = (v: number | null, extra = '') =>
    numCell(v, extra, v === null ? VERSIONS_NA_TOOLTIP : undefined);

  return (
    <div className="space-y-4">
      <div className="flex gap-1 border-b border-slate-200" role="tablist" aria-label="Cron">
        {([
          ['A', 'Discovery & Ingest'],
          ['B', 'Parser Rebuild & Promotion'],
        ] as const).map(([c, label]) => (
          <button
            key={c}
            type="button"
            role="tab"
            aria-selected={cronTab === c}
            onClick={() => setCronTab(c)}
            className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 text-sm font-semibold transition-colors ${
              cronTab === c
                ? 'border-indigo-600 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${
              cronTab === c
                ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                : 'bg-slate-100 text-slate-600 border-slate-200'
            }`}>Cron {c}</span>
            {label}
          </button>
        ))}
      </div>

      {/* CRON A — Discovery & Ingest */}
      {cronTab === 'A' && (
      <div>
        <p className="text-xs text-slate-500 mb-2" data-testid="cron-a-synced-at">
          {lastSyncedAt
            ? `Cron A runs on the backfill VM; copied here by the merge job, last synced ${formatDate(lastSyncedAt)}.`
            : 'Cron A runs on the backfill VM; its runs are copied here by the merge job (not synced yet).'}
        </p>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-slate-400">
            new drugs found → downloaded → parsed → ingested
            ({cronAView === 'latest' ? 'per family, latest run' : cronAView === 'verified' ? 'verified against the database' : `per family, ${cronAView}`})
          </span>
          <div className="ml-auto inline-flex rounded border border-slate-200 overflow-hidden" role="group" aria-label="Cron A view">
            {(['latest', 'daily', 'weekly', 'verified'] as const).map(v => (
              <button
                key={v}
                type="button"
                aria-pressed={cronAView === v}
                onClick={() => setCronAView(v)}
                className={`px-3 py-1 text-xs font-medium ${cronAView === v ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                {v === 'latest' ? 'Latest' : v === 'daily' ? 'Daily' : v === 'weekly' ? 'Weekly' : 'Verified Details'}
              </button>
            ))}
          </div>
        </div>
        {cronAView === 'verified' ? (
          <CorpusVerificationPanel corpus="us_spl" />
        ) : cronAView !== 'latest' ? (
          <CronASummaryTable key={cronAView} period={cronAView} />
        ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Family</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Last run</th>
                <th className="text-right px-4 py-3 font-medium" title={cronATips.newProducts}>New products</th>
                <th className="text-right px-4 py-3 font-medium" title={cronATips.newVersions}>New versions</th>
                <th className="text-right px-4 py-3 font-medium" title={cronATips.productsIngested}>Products ingested</th>
                <th className="text-right px-4 py-3 font-medium" title={cronATips.versionsIngested}>Versions ingested</th>
                <th className="text-right px-4 py-3 font-medium">Failed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {cronA.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                    No Cron A discovery runs yet.
                  </td>
                </tr>
              )}
              {cronA.map(job => (
                <React.Fragment key={job.key}>
                  <tr
                    className={`hover:bg-slate-50 cursor-pointer ${isBad(job.status) ? 'bg-amber-50/40' : ''}`}
                    onClick={() => setExpanded(expanded === job.key ? null : job.key)}
                  >
                    <td className="px-4 py-3 text-slate-700" title={job.family}>{familyLabel(job.family)}</td>
                    <td className="px-4 py-3">{reconStatusBadge(job.status)}</td>
                    <td className="px-4 py-3 text-slate-700">{formatDate(job.startedAt)}</td>
                    {numCell(job.split.productsDiscovered)}
                    {versionCell(job.split.versionsDiscovered)}
                    {numCell(job.split.productsIngested, 'text-green-700')}
                    {versionCell(job.split.versionsIngested, 'text-green-700')}
                    {numCell(job.failed, 'text-red-700')}
                  </tr>
                  {expandRow(job, 8)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
      )}

      {/* CRON B — DB-store reconciliation: one tab per job */}
      {cronTab === 'B' && (
      <div>
        <div className="flex gap-1 border-b border-slate-200 mb-3" role="tablist" aria-label="Cron B stage">
          {CRON_B_STAGES.map(st => (
            <button
              key={st.key}
              type="button"
              role="tab"
              aria-selected={cronBStage === st.key}
              title={st.hint}
              onClick={() => setUrl({ cron: 'B', stage: st.key, view: cronBView })}
              className={`px-4 py-1.5 -mb-px border-b-2 text-sm font-medium transition-colors ${
                cronBStage === st.key
                  ? 'border-indigo-600 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {st.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-slate-400">
            {CRON_B_STAGES.find(st => st.key === cronBStage)?.hint}
            {' '}({cronBView === 'latest' ? 'per route, latest run' : `per route, ${cronBView}`})
          </span>
          <div className="ml-auto inline-flex rounded border border-slate-200 overflow-hidden" role="group" aria-label="Cron B view">
            {(['latest', 'daily', 'weekly'] as const).map(v => (
              <button
                key={v}
                type="button"
                aria-pressed={cronBView === v}
                onClick={() => setUrl({ cron: 'B', stage: cronBStage, view: v })}
                className={`px-3 py-1 text-xs font-medium ${cronBView === v ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >
                {v === 'latest' ? 'Latest' : v === 'daily' ? 'Daily' : 'Weekly'}
              </button>
            ))}
          </div>
        </div>
        {cronBView === 'latest' ? (
          <CronBStageTable key={cronBStage} stage={cronBStage} />
        ) : (
          <CronBSummaryTable key={`${cronBStage}-${cronBView}`} stage={cronBStage} period={cronBView} />
        )}
      </div>
      )}
    </div>
  );
};

const AuditRunDetail: React.FC<{
  run: AuditRun;
  onBack: () => void;
  formatDate: (d: string | null) => string;
  statusBadge: (s: string) => JSX.Element;
}> = ({ run, onBack, formatDate, statusBadge }) => {
  const flaggedDrugs = run.flagged_drugs ? Object.entries(run.flagged_drugs) : [];
  const [search, setSearch] = useState('');

  const filtered = flaggedDrugs.filter(([setId, drug]) =>
    setId.includes(search.toLowerCase()) ||
    (drug.reasons || []).some((r: string) => r.includes(search.toLowerCase()))
  );

  return (
    <div>
      <button onClick={onBack} className="text-sm text-indigo-600 hover:underline mb-4">
        &larr; Back to all runs
      </button>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card label="Status">{statusBadge(run.status)}</Card>
        <Card label="Started">{formatDate(run.started_at)}</Card>
        <Card label="Flagged">{run.total_flagged?.toLocaleString()}</Card>
        <Card label="Mismatched">{run.total_mismatched?.toLocaleString()}</Card>
        <Card label="Re-ingested">{run.total_reingested?.toLocaleString()}</Card>
      </div>

      {/* Verdicts */}
      {run.verdicts && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
          <h3 className="text-sm font-medium text-slate-700 mb-2">Verdicts</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(run.verdicts).map(([k, v]) => (
              <div key={k} className="text-sm">
                <span className="font-mono text-slate-900">{v.toLocaleString()}</span>{' '}
                <span className="text-slate-500">{k}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Anomaly reasons */}
      {run.anomaly_reasons && (
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
          <h3 className="text-sm font-medium text-slate-700 mb-2">Anomaly Breakdown</h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(run.anomaly_reasons).map(([k, v]) => (
              <div key={k} className="text-sm">
                <span className="font-mono text-slate-900">{v.toLocaleString()}</span>{' '}
                <span className="text-slate-500">{k}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Flagged drugs table */}
      {flaggedDrugs.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-700">
              Flagged Drugs ({filtered.length.toLocaleString()})
            </h3>
            <input
              type="text"
              placeholder="Search set_id or reason..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-sm border border-slate-300 rounded px-3 py-1 w-64"
            />
          </div>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Set ID</th>
                  <th className="text-left px-3 py-2 font-medium">Drug ID</th>
                  <th className="text-left px-3 py-2 font-medium">Version</th>
                  <th className="text-left px-3 py-2 font-medium">Reasons</th>
                  <th className="text-left px-3 py-2 font-medium">Verdict</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.slice(0, 200).map(([setId, drug]) => (
                  <tr key={setId} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono">{setId}</td>
                    <td className="px-3 py-2">{drug.drug_id}</td>
                    <td className="px-3 py-2">{drug.version_number}{drug.remote_version ? ` -> ${drug.remote_version}` : ''}</td>
                    <td className="px-3 py-2">
                      {(drug.reasons || []).map((r: string) => (
                        <span key={r} className="inline-block bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 mr-1 text-[10px]">
                          {r}
                        </span>
                      ))}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs ${
                        drug.verdict === 'MISMATCH' ? 'text-red-600' :
                        drug.verdict === 'MATCH' ? 'text-green-600' :
                        drug.verdict === 'REINGESTED' ? 'text-blue-600' :
                        'text-slate-400'
                      }`}>
                        {drug.verdict || 'PENDING'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length > 200 && (
              <div className="px-3 py-2 text-xs text-slate-400 text-center">
                Showing first 200 of {filtered.length.toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}

      {run.error_message && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm font-mono whitespace-pre-wrap">
          {run.error_message}
        </div>
      )}
    </div>
  );
};

// Counter columns shown in the VD integrity table, in display order. Mirrors
// the script's `COUNTERS` documentation (audit_version_diffs_integrity.py
// header). Hoisted out of the component so it isn't reallocated per render.
// `hardIfPositive` columns turn red on any positive value; the residual
// columns (unrepairable_fk / unrepairable_content / manual_inspect) only
// hard-alert on *growth* day-over-day, not on absolute value, so they stay
// neutral here — the Alerts column surfaces growth signals via the badge.
const VD_COUNTER_COLS: Array<{ key: string; label: string; title?: string; hardIfPositive?: boolean }> = [
  { key: 'total_rows', label: 'Total' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'content_recomputed', label: 'Recomputed' },
  { key: 'corrupt_rows', label: 'Corrupt', hardIfPositive: true, title: 'Hard alert if > 0' },
  { key: 'unrepairable_fk_residual', label: 'Unrep. FK', title: 'Hard alert on day-over-day growth, not absolute value' },
  { key: 'unrepairable_content_residual', label: 'Unrep. content', title: 'Hard alert on day-over-day growth, not absolute value' },
  { key: 'manual_inspect_residual', label: 'Manual inspect', title: 'Hard alert on day-over-day growth, not absolute value' },
  { key: 'never_processed', label: 'Never proc.', hardIfPositive: true, title: 'Hard alert if > 0 (Stage 5b should keep this at 0)' },
  { key: 'pending_content_repair', label: 'Pending', hardIfPositive: true, title: 'Hard alert if > 0 (Stage 5b should keep this at 0)' },
];

const VDIntegrityTab: React.FC<{
  reports: VDIntegrityReport[];
  bucket: string | null;
  fetchError: string | null;
  formatDate: (d: string | null) => string;
}> = ({ reports, bucket, fetchError, formatDate }) => {

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Version-Diffs Integrity</h2>
          <p className="text-sm text-slate-500">
            Daily counters from the 06:00 UTC cron
            (<code className="font-mono text-xs">audit_version_diffs_integrity.py</code>).
          </p>
        </div>
        {bucket && (
          <span className="text-xs text-slate-400 font-mono">gs://{bucket}/</span>
        )}
      </div>

      {fetchError && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
          GCS read partially failed: {fetchError}
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Date</th>
              {VD_COUNTER_COLS.map(c => (
                <th
                  key={c.key}
                  title={c.title}
                  className={`text-right px-3 py-2 font-medium whitespace-nowrap ${c.title ? 'cursor-help underline decoration-dotted decoration-slate-300' : ''}`}
                >
                  {c.label}
                </th>
              ))}
              <th className="text-left px-3 py-2 font-medium">Alerts</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {reports.length === 0 && (
              <tr>
                <td colSpan={VD_COUNTER_COLS.length + 2} className="px-4 py-8 text-center text-slate-400">
                  No reports found in <code className="font-mono text-xs">{bucket ?? '<unknown bucket>'}</code>.
                </td>
              </tr>
            )}
            {reports.map(r => (
              <tr key={r.blob_name} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">
                  {r.date ?? '—'}
                  {r.audited_at && (
                    <div className="text-xs text-slate-400">{formatDate(r.audited_at)}</div>
                  )}
                </td>
                {VD_COUNTER_COLS.map(c => {
                  const val = r.counters?.[c.key];
                  const isHard = c.hardIfPositive && typeof val === 'number' && val > 0;
                  return (
                    <td
                      key={c.key}
                      className={`px-3 py-2 text-right font-mono ${isHard ? 'text-red-700 font-semibold' : 'text-slate-700'}`}
                    >
                      {typeof val === 'number' ? val.toLocaleString() : '—'}
                    </td>
                  );
                })}
                <td className="px-3 py-2">
                  {r.hard_alert_count > 0 ? (
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700"
                      title={r.alerts.filter(a => a.severity === 'hard').map(a => `${a.counter}: ${a.message}`).join('\n')}
                    >
                      {r.hard_alert_count} hard
                    </span>
                  ) : r.alerts.length > 0 ? (
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600"
                      title={r.alerts.map(a => `${a.counter}: ${a.message}`).join('\n')}
                    >
                      {r.alerts.length} info
                    </span>
                  ) : (
                    <span className="text-xs text-slate-400">none</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DatabaseTab: React.FC<{ report: DbReport }> = ({ report }) => (
  <div className="space-y-4">
    {/* Summary cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card label="Database Size">{report.database_size}</Card>
      {report.drugs_by_region.map(r => (
        <Card key={r.region} label={`${r.region} Drugs`}>{r.count.toLocaleString()}</Card>
      ))}
    </div>

    {/* Row counts */}
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <h3 className="text-sm font-medium text-slate-700 mb-3">Row Counts</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {Object.entries(report.row_counts).map(([table, count]) => (
          <div key={table} className="flex justify-between text-sm">
            <span className="text-slate-600">{table}</span>
            <span className="font-mono text-slate-900">{count?.toLocaleString() ?? '—'}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Backups */}
    {(report as any).backups && (
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <h3 className="text-sm font-medium text-slate-700 px-4 py-3 border-b border-slate-200">
          Backups (GCS)
        </h3>
        <div className="p-4">
          {(report as any).backups.existing_backups?.length > 0 ? (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">File</th>
                  <th className="text-right px-3 py-2 font-medium">Size</th>
                  <th className="text-left px-3 py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(report as any).backups.existing_backups.map((b: any, i: number) => (
                  <tr key={i}>
                    <td className="px-3 py-2 font-mono">{b.path}</td>
                    <td className="px-3 py-2 text-right">{b.size}</td>
                    <td className="px-3 py-2 text-slate-500">{b.created ? new Date(b.created).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-sm text-slate-400">No backups found</p>
          )}
          {(report as any).backups.error && (
            <p className="text-xs text-amber-600 mt-2">{(report as any).backups.error}</p>
          )}
        </div>
      </div>
    )}

    {/* Table sizes */}
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <h3 className="text-sm font-medium text-slate-700 px-4 py-3 border-b border-slate-200">
        Table Sizes (Top 20)
      </h3>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Table</th>
            <th className="text-right px-4 py-2 font-medium">Size</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {report.tables.map(t => (
            <tr key={t.table}>
              <td className="px-4 py-2 font-mono text-xs">{t.table}</td>
              <td className="px-4 py-2 text-right">{t.size}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ACTION_LABELS: Record<string, string> = {
  search: 'Search',
  keyword: 'Keyword',
  research: 'Investigator',
  qa: 'CEPI',
  compare_ai: 'Compare AI',
  chat: 'Chat with Results',
  sba: 'SBA Review',
};

const ActivityTab: React.FC<{
  users: UserActivity[];
  calls: any[];
  savedItems: SavedItemsData | null;
  savedItemsError: boolean;
  notifications: NotificationsData | null;
  notificationsError: boolean;
  days: number;
  onDaysChange: (d: number) => void;
  filterEmail: string;
  onFilterEmail: (e: string) => void;
  orgOptions: { id: number; name: string }[];
  organization: string;
  onOrganizationChange: (o: string) => void;
  formatDate: (d: string | null) => string;
}> = ({ users, calls, savedItems, savedItemsError, notifications, notificationsError, days, onDaysChange, filterEmail, onFilterEmail, orgOptions, organization, onOrganizationChange, formatDate }) => {
  const [groupByTenant, setGroupByTenant] = useState(false);
  const NO_TENANT = '(no tenant)';
  const groups = new Map<string, UserActivity[]>();
  if (groupByTenant) {
    for (const u of users) {
      const k = u.organization_id == null ? 'none' : String(u.organization_id);
      groups.set(k, [...(groups.get(k) || []), u]);
    }
  }
  const renderUser = (u: UserActivity, indent = false) => (
    <tr key={`${u.email}|${u.organization_id ?? ''}`}>
      <td className={`px-4 py-2 ${indent ? 'pl-8' : ''}`}>{u.email}</td>
      {!groupByTenant && <td className="px-4 py-2 text-xs text-slate-600">{u.organization_name || '—'}</td>}
      <td className="px-4 py-2 text-right font-mono">{u.call_count?.toLocaleString()}</td>
      <td className="px-4 py-2 text-right font-mono">{u.request_count?.toLocaleString()}</td>
      <td className="px-4 py-2 text-right font-mono">${u.total_cost?.toFixed(2)}</td>
      <td className="px-4 py-2 text-slate-500 text-xs">{formatDate(u.last_activity)}</td>
      <td className="px-4 py-2">
        <button onClick={() => onFilterEmail(u.email || '')} className="text-xs text-indigo-600 hover:underline">
          Filter
        </button>
      </td>
    </tr>
  );
  return (
  <div className="space-y-4">
    {/* Controls */}
    <div className="flex items-center gap-4">
      <select
        value={days}
        onChange={e => onDaysChange(Number(e.target.value))}
        className="text-sm border border-slate-300 rounded px-3 py-1.5"
      >
        <option value={1}>Last 24h</option>
        <option value={7}>Last 7 days</option>
        <option value={30}>Last 30 days</option>
        <option value={90}>Last 90 days</option>
        <option value={180}>Last 6 months</option>
        <option value={365}>Last year</option>
      </select>
      <select
        aria-label="Tenant"
        value={organization}
        onChange={e => onOrganizationChange(e.target.value)}
        className="text-sm border border-slate-300 rounded px-3 py-1.5"
      >
        <option value="">All tenants</option>
        {orgOptions.map(o => (
          <option key={o.id} value={String(o.id)}>{o.name}</option>
        ))}
        <option value="none">{NO_TENANT}</option>
      </select>
      <label className="flex items-center gap-1.5 text-sm text-slate-600">
        <input type="checkbox" checked={groupByTenant} onChange={e => setGroupByTenant(e.target.checked)} />
        Group by tenant
      </label>
    </div>

    {/* User summary */}
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <h3 className="text-sm font-medium text-slate-700 px-4 py-3 border-b border-slate-200">
        User Activity ({days}d)
      </h3>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-4 py-2 font-medium">Email</th>
            {!groupByTenant && <th className="text-left px-4 py-2 font-medium">Tenant</th>}
            <th className="text-right px-4 py-2 font-medium">LLM Calls</th>
            <th className="text-right px-4 py-2 font-medium">Requests</th>
            <th className="text-right px-4 py-2 font-medium">Cost</th>
            <th className="text-left px-4 py-2 font-medium">Last Active</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {groupByTenant
            ? Array.from(groups.entries()).map(([key, members]) => (
                <React.Fragment key={key}>
                  <tr className="bg-slate-50 font-medium" data-testid="tenant-header">
                    <td className="px-4 py-2">{members[0]?.organization_name || NO_TENANT}</td>
                    <td className="px-4 py-2 text-right font-mono"></td>
                    <td className="px-4 py-2 text-right font-mono" data-testid="tenant-requests">
                      {members.reduce((n, m) => n + (m.request_count || 0), 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right font-mono" data-testid="tenant-cost">
                      ${members.reduce((n, m) => n + (m.total_cost || 0), 0).toFixed(2)}
                    </td>
                    <td colSpan={2}></td>
                  </tr>
                  {members.map(m => renderUser(m, true))}
                </React.Fragment>
              ))
            : users.map(u => renderUser(u))}
        </tbody>
      </table>
    </div>

    {/* Recent Actions */}
    <ActionsList calls={calls} filterEmail={filterEmail} onFilterEmail={onFilterEmail} formatDate={formatDate} />

    <SavedItemsPanel data={savedItems} error={savedItemsError} filterEmail={filterEmail} onFilterEmail={onFilterEmail} formatDate={formatDate} />
    <NotificationsPanel data={notifications} error={notificationsError} filterEmail={filterEmail} onFilterEmail={onFilterEmail} formatDate={formatDate} />
  </div>
  );
};

const ActionsList: React.FC<{
  calls: any[];
  filterEmail: string;
  onFilterEmail: (e: string) => void;
  formatDate: (d: string | null) => string;
}> = ({ calls, filterEmail, onFilterEmail, formatDate }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [breakdown, setBreakdown] = useState<any>(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);

  const toggleExpand = async (requestId: string | null) => {
    if (!requestId || expandedId === requestId) {
      setExpandedId(null);
      setBreakdown(null);
      return;
    }
    setExpandedId(requestId);
    setLoadingBreakdown(true);
    try {
      const res = await authApiClient.get(`/admin/user-activity/cost-breakdown/${requestId}`);
      setBreakdown(res.data);
    } catch {
      setBreakdown(null);
    } finally {
      setLoadingBreakdown(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <h3 className="text-sm font-medium text-slate-700">
          Recent Actions
          {filterEmail && <span className="text-indigo-600 ml-1">({filterEmail})</span>}
        </h3>
        {filterEmail && (
          <button onClick={() => onFilterEmail('')} className="text-xs text-slate-500 hover:underline">
            Clear filter
          </button>
        )}
      </div>
      <div className="max-h-[500px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 sticky top-0">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Time</th>
              <th className="text-left px-4 py-2 font-medium">User</th>
              <th className="text-left px-4 py-2 font-medium">Tenant</th>
              <th className="text-left px-4 py-2 font-medium">Action</th>
              <th className="text-left px-4 py-2 font-medium">Query</th>
              <th className="text-right px-4 py-2 font-medium">Results</th>
              <th className="text-left px-4 py-2 font-medium">Tools</th>
              <th className="text-right px-4 py-2 font-medium">Tokens</th>
              <th className="text-right px-4 py-2 font-medium">Latency</th>
              <th className="text-right px-4 py-2 font-medium">Cost</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {calls.map((a: any, i: number) => (
              <React.Fragment key={i}>
                <tr
                  className={`hover:bg-slate-50 cursor-pointer ${expandedId === a.request_id ? 'bg-indigo-50' : ''}`}
                  onClick={() => toggleExpand(a.request_id)}
                >
                  <td className="px-4 py-2 text-slate-500 text-xs whitespace-nowrap">{formatDate(a.created_at)}</td>
                  <td className="px-4 py-2 text-xs">{a.user_email || '—'}</td>
                  <td className="px-4 py-2 text-xs text-slate-600">{a.organization_name || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                      a.action === 'search' ? 'bg-blue-100 text-blue-700' :
                      a.action === 'keyword' ? 'bg-rose-100 text-rose-700' :
                      a.action === 'research' ? 'bg-purple-100 text-purple-700' :
                      a.action === 'qa' ? 'bg-green-100 text-green-700' :
                      a.action === 'compare_ai' ? 'bg-teal-100 text-teal-700' :
                      a.action === 'chat' ? 'bg-amber-100 text-amber-700' :
                      a.action === 'sba' ? 'bg-cyan-100 text-cyan-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {ACTION_LABELS[a.action] || a.action}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs align-top max-w-lg break-words whitespace-pre-wrap">{a.query}</td>
                  <td className="px-4 py-2 text-right text-xs font-mono">{a.result_count ?? '—'}</td>
                  <td
                    className="px-4 py-2 text-xs text-slate-600 align-top max-w-xs break-words"
                    data-testid="cepi-tools-cell"
                  >
                    {formatToolNames(a.cepi_trace_summary)}
                  </td>
                  <td
                    className="px-4 py-2 text-right text-xs font-mono"
                    data-testid="cepi-tokens-cell"
                  >
                    {formatTotalTokens(a.cepi_trace_summary)}
                  </td>
                  <td
                    className="px-4 py-2 text-right text-xs font-mono"
                    data-testid="cepi-latency-cell"
                  >
                    {formatLatency(a.cepi_trace_summary ?? a)}
                  </td>
                  <td className="px-4 py-2 text-right text-xs font-mono">
                    {a.cost_usd > 0
                      ? `$${a.cost_usd < 0.01 ? a.cost_usd.toFixed(4) : a.cost_usd.toFixed(2)}`
                      : '—'}
                    <span className="ml-1 text-slate-400">{a.request_id ? '▸' : ''}</span>
                  </td>
                </tr>
                {expandedId === a.request_id && (
                  <tr>
                    <td colSpan={10} className="px-4 py-3 bg-slate-50">
                      {loadingBreakdown ? (
                        <div className="text-xs text-slate-400">Loading breakdown...</div>
                      ) : breakdown?.calls?.length > 0 ? (
                        <table className="w-full text-xs">
                          <thead className="text-slate-500">
                            <tr>
                              <th className="text-left py-1 font-medium">Stage</th>
                              <th className="text-left py-1 font-medium">Model</th>
                              <th className="text-right py-1 font-medium">Input</th>
                              <th className="text-right py-1 font-medium">Output</th>
                              <th className="text-right py-1 font-medium">Cached</th>
                              <th className="text-right py-1 font-medium">Cost</th>
                            </tr>
                          </thead>
                          <tbody>
                            {breakdown.calls.map((c: any, j: number) => (
                              <tr key={j} className="border-t border-slate-200">
                                <td className="py-1 font-mono">{c.stage}</td>
                                <td className="py-1 font-mono text-slate-500">{c.model}</td>
                                <td className="py-1 text-right">{(c.input_tokens / 1000).toFixed(1)}k</td>
                                <td className="py-1 text-right">{(c.output_tokens / 1000).toFixed(1)}k</td>
                                <td className="py-1 text-right text-slate-400">{c.cached_tokens > 0 ? `${(c.cached_tokens / 1000).toFixed(1)}k` : '—'}</td>
                                <td className="py-1 text-right font-mono">${c.cost_usd.toFixed(4)}</td>
                              </tr>
                            ))}
                            <tr className="border-t-2 border-slate-300 font-semibold">
                              <td className="py-1" colSpan={5}>Total ({breakdown.total_calls} calls)</td>
                              <td className="py-1 text-right font-mono">${breakdown.total_cost.toFixed(4)}</td>
                            </tr>
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-xs text-slate-400">No cost data for this request</div>
                      )}
                      {/* CEPI trace summary — present only when the row
                          originated from the unified-chat pipeline with
                          CEPI_TRACE_ENABLED. Non-CEPI rows omit this block
                          entirely so the expand area still scans cleanly. */}
                      {a.cepi_trace_summary && (
                        <details className="mt-3" data-testid="cepi-trace-details">
                          <summary className="cursor-pointer text-xs font-medium text-indigo-700 hover:text-indigo-900">
                            CEPI trace summary (view JSON)
                          </summary>
                          <pre className="mt-2 max-h-96 overflow-auto bg-white border border-slate-200 rounded p-3 text-[11px] font-mono text-slate-700 whitespace-pre-wrap break-words">
                            {JSON.stringify(a.cepi_trace_summary, null, 2)}
                          </pre>
                        </details>
                      )}
                      {/* Cogni drug-search results shown (query_logs.metadata_json.results). */}
                      {Array.isArray(a.metadata?.results) && a.metadata.results.length > 0 && (
                        <details className="mt-3" data-testid="cogni-results-details">
                          <summary className="cursor-pointer text-xs font-medium text-indigo-700 hover:text-indigo-900">
                            Results shown ({typeof a.result_count === 'number' && a.result_count > a.metadata.results.length ? `top ${a.metadata.results.length} of ${a.result_count}` : `${a.metadata.results.length} drugs`})
                          </summary>
                          <table className="mt-2 w-full text-xs">
                            <thead>
                              <tr className="text-left text-slate-500">
                                <th className="py-1 font-medium">#</th>
                                <th className="py-1 text-right font-medium">Score</th>
                                <th className="py-1 pl-3 font-medium">Drug</th>
                                <th className="py-1 font-medium">Region</th>
                                <th className="py-1 font-medium">drug_id</th>
                              </tr>
                            </thead>
                            <tbody>
                              {a.metadata.results.map((r: any, j: number) => (
                                <tr key={j} className="border-t border-slate-200">
                                  <td className="py-1 text-slate-400">{j + 1}</td>
                                  <td className="py-1 text-right font-mono">{r.score ?? '—'}</td>
                                  <td className="py-1 pl-3">{r.drug_name}</td>
                                  <td className="py-1 font-mono text-slate-500">{r.region}</td>
                                  <td className="py-1 font-mono text-slate-500">{r.drug_id}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </details>
                      )}
                      {/* Keyword-search request parameters (query_logs.metadata_json). */}
                      {a.action === 'keyword' && a.metadata && (
                        <details className="mt-3" data-testid="keyword-params-details">
                          <summary className="cursor-pointer text-xs font-medium text-indigo-700 hover:text-indigo-900">
                            Search parameters (view JSON)
                          </summary>
                          <pre className="mt-2 max-h-96 overflow-auto bg-white border border-slate-200 rounded p-3 text-[11px] font-mono text-slate-700 whitespace-pre-wrap break-words">
                            {JSON.stringify(a.metadata, null, 2)}
                          </pre>
                        </details>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CostsTab: React.FC<{
  summary: any;
  daily: any[];
  days: number;
  onDaysChange: (d: number) => void;
  groupBy: 'user' | 'pipeline' | 'model' | 'stage';
  onGroupByChange: (g: 'user' | 'pipeline' | 'model' | 'stage') => void;
  reconciliation: any;
  reconBucket: 'daily' | 'weekly' | 'monthly';
  onReconBucketChange: (b: 'daily' | 'weekly' | 'monthly') => void;
  userFilter: string;
  onUserFilter: (e: string) => void;
}> = ({ summary, daily, days, onDaysChange, groupBy, onGroupByChange, reconciliation, reconBucket, onReconBucketChange, userFilter, onUserFilter }) => (
  <div className="space-y-4">
    {/* Controls */}
    <div className="flex items-center gap-4 flex-wrap">
      <select
        value={days}
        onChange={e => onDaysChange(Number(e.target.value))}
        className="text-sm border border-slate-300 rounded px-3 py-1.5"
      >
        <option value={1}>Last 24h</option>
        <option value={7}>Last 7 days</option>
        <option value={30}>Last 30 days</option>
        <option value={90}>Last 90 days</option>
        <option value={180}>Last 6 months</option>
        <option value={365}>Last year</option>
      </select>
      <select
        value={groupBy}
        onChange={e => onGroupByChange(e.target.value as any)}
        className="text-sm border border-slate-300 rounded px-3 py-1.5"
      >
        <option value="user">Group by User</option>
        <option value="pipeline">Group by Pipeline</option>
        <option value="model">Group by Model</option>
        <option value="stage">Group by Stage</option>
      </select>
      {userFilter && (
        <div className="flex items-center gap-2 text-sm bg-indigo-50 text-indigo-700 rounded px-3 py-1.5">
          <span>{userFilter}</span>
          <button onClick={() => onUserFilter('')} className="text-indigo-400 hover:text-indigo-600">x</button>
        </div>
      )}
    </div>

    {/* Summary cards */}
    {summary && (
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card label="Total Cost">${summary.total_cost_usd?.toFixed(2)}</Card>
        <Card label="LLM Calls">{summary.total_calls?.toLocaleString()}</Card>
        <Card label="Input Tokens">{(summary.total_input_tokens / 1000000)?.toFixed(2)}M</Card>
        <Card label="Output Tokens">{(summary.total_output_tokens / 1000000)?.toFixed(2)}M</Card>
        <Card label="Cached Tokens">{(summary.total_cached_tokens / 1000000)?.toFixed(2)}M</Card>
      </div>
    )}

    {/* Breakdown table */}
    {summary?.breakdown && (
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <h3 className="text-sm font-medium text-slate-700 px-4 py-3 border-b border-slate-200">
          Cost by {groupBy}
        </h3>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-2 font-medium">{groupBy === 'user' ? 'Email' : groupBy === 'pipeline' ? 'Pipeline' : groupBy === 'model' ? 'Model' : 'Pipeline / Stage'}</th>
              <th className="text-right px-4 py-2 font-medium">Cost</th>
              <th className="text-right px-4 py-2 font-medium">Calls</th>
              <th className="text-right px-4 py-2 font-medium">Input</th>
              <th className="text-right px-4 py-2 font-medium">Output</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {summary.breakdown.map((r: any, i: number) => (
              <tr key={i} className={groupBy === 'user' ? 'cursor-pointer hover:bg-slate-50' : ''} onClick={() => groupBy === 'user' && onUserFilter(r.key)}>
                <td className="px-4 py-2">{r.key}</td>
                <td className="px-4 py-2 text-right font-mono">${r.cost_usd?.toFixed(4)}</td>
                <td className="px-4 py-2 text-right font-mono">{r.calls?.toLocaleString()}</td>
                <td className="px-4 py-2 text-right text-slate-500">{(r.input_tokens / 1000)?.toFixed(0)}k</td>
                <td className="px-4 py-2 text-right text-slate-500">{(r.output_tokens / 1000)?.toFixed(0)}k</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Daily cost bars */}
    {daily.length > 0 && (
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <h3 className="text-sm font-medium text-slate-700 px-4 py-3 border-b border-slate-200">
          Daily Cost
        </h3>
        <div className="p-4 space-y-2">
          {daily.map((d: any) => {
            const maxCost = Math.max(...daily.map((x: any) => x.cost_usd || 0), 0.01);
            const pct = ((d.cost_usd || 0) / maxCost) * 100;
            return (
              <div key={d.day} className="flex items-center gap-3 text-xs">
                <span className="w-20 text-slate-500 shrink-0">{d.day}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                  <div
                    className="bg-indigo-500 h-full rounded-full"
                    style={{ width: `${Math.max(pct, 1)}%` }}
                  />
                </div>
                <span className="w-16 text-right font-mono">${d.cost_usd?.toFixed(2)}</span>
                <span className="w-16 text-right text-slate-400">{d.calls} calls</span>
              </div>
            );
          })}
        </div>
      </div>
    )}

    {!summary && !daily.length && (
      <div className="text-center text-slate-400 py-8">
        No LLM usage data yet. Data will appear after the tracking code is deployed.
      </div>
    )}

    {/* Reconciliation: Tracked vs Actual */}
    {!reconciliation && (
      <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent mb-2"></div>
        <p className="text-sm text-slate-500">Loading reconciliation data...</p>
      </div>
    )}
    {reconciliation && (
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-sm font-medium text-slate-700">Tracked vs Actual (OpenAI Costs API)</h3>
          <select
            value={reconBucket}
            onChange={e => onReconBucketChange(e.target.value as any)}
            className="text-xs border border-slate-300 rounded px-2 py-1"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div className="px-4 py-2 border-b border-slate-100">
          {reconciliation.openai_error && (
            <p className="text-xs text-amber-600 mt-1">{reconciliation.openai_error}</p>
          )}
          {reconciliation.gemini_note && (
            <p className="text-xs text-slate-400 mt-1">{reconciliation.gemini_note}</p>
          )}
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-b border-slate-100">
          <div>
            <div className="text-xs text-slate-500">Tracked Total</div>
            <div className="text-lg font-semibold">${reconciliation.total_tracked?.toFixed(2)}</div>
          </div>
          {reconciliation.total_actual_openai != null && (
            <div>
              <div className="text-xs text-slate-500">Actual OpenAI</div>
              <div className="text-lg font-semibold">${reconciliation.total_actual_openai?.toFixed(2)}</div>
            </div>
          )}
          {reconciliation.total_diff_openai != null && (
            <div>
              <div className="text-xs text-slate-500">Difference</div>
              <div className={`text-lg font-semibold ${reconciliation.total_diff_openai > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {reconciliation.total_diff_openai > 0 ? '+' : ''}${reconciliation.total_diff_openai?.toFixed(2)}
              </div>
            </div>
          )}
        </div>

        {/* Daily breakdown */}
        {reconciliation.data?.length > 0 && (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Period (UTC)</th>
                <th className="text-right px-4 py-2 font-medium">Tracked (OpenAI)</th>
                <th className="text-right px-4 py-2 font-medium">Tracked (Google)</th>
                <th className="text-right px-4 py-2 font-medium">Actual (OpenAI)</th>
                <th className="text-right px-4 py-2 font-medium">Diff</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {reconciliation.data.map((d: any) => (
                <tr key={d.period}>
                  <td className="px-4 py-2">{d.period}</td>
                  <td className="px-4 py-2 text-right font-mono">${d.tracked_openai?.toFixed(4)}</td>
                  <td className="px-4 py-2 text-right font-mono">${d.tracked_google?.toFixed(4)}</td>
                  <td className="px-4 py-2 text-right font-mono">{d.actual_openai != null ? `$${d.actual_openai?.toFixed(4)}` : '—'}</td>
                  <td className={`px-4 py-2 text-right font-mono ${d.diff_openai > 0.01 ? 'text-red-600' : d.diff_openai < -0.01 ? 'text-green-600' : ''}`}>
                    {d.diff_openai != null ? `${d.diff_openai > 0 ? '+' : ''}$${d.diff_openai?.toFixed(4)}` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    )}
  </div>
);

const Card: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="bg-white rounded-lg border border-slate-200 px-4 py-3">
    <div className="text-xs text-slate-500 mb-1">{label}</div>
    <div className="text-lg font-semibold text-slate-900">{children}</div>
  </div>
);


// Cache management — admins type a query, the backend purges any L1
// (search_cache) and L2 (semantic_cache_queries) rows that match the
// normalized form of the query. Used when a specific query starts
// returning stale or wrong results and we want to force the next
// hit to recompute.
const CacheTab: React.FC = () => {
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    query: string;
    normalized_query: string;
    l1_rows_deleted: number;
    l2_rows_deleted: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handlePurge = async () => {
    if (!query.trim() || busy) return;
    if (!window.confirm(`Purge cache for "${query.trim()}"?`)) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await authApiClient.delete('/admin/cache/search', {
        params: { query: query.trim() },
      });
      setResult(res.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to purge cache');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Clear search cache</h3>
        <p className="text-sm text-slate-500 mb-4">
          Type a query exactly as a user would type it. The cache layer normalizes
          the text (lowercase, punctuation stripped, whitespace collapsed) before
          looking up rows, so minor differences in casing or spacing still match.
          Both L1 (exact-match) and L2 (semantic) cache rows for that normalized
          query are deleted; the next time anyone searches for it, the pipeline
          re-runs and writes fresh cache entries.
        </p>

        <div className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handlePurge(); }}
            placeholder="e.g. unique management for ocular toxicity"
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm
                       focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
            disabled={busy}
          />
          <button
            onClick={handlePurge}
            disabled={busy || query.trim().length === 0}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg
                       hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? 'Purging…' : 'Clear cache'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 text-sm">
          <div className="font-medium text-emerald-900 mb-1">
            Purge complete for "{result.query}"
          </div>
          <div className="text-emerald-700">
            Normalized: <code className="font-mono text-xs bg-white px-1 py-0.5 rounded">{result.normalized_query}</code>
          </div>
          <div className="text-emerald-700 mt-1">
            L1 (search_cache): <strong>{result.l1_rows_deleted}</strong> row{result.l1_rows_deleted === 1 ? '' : 's'} deleted.
            L2 (semantic_cache_queries): <strong>{result.l2_rows_deleted}</strong> row{result.l2_rows_deleted === 1 ? '' : 's'} deleted.
          </div>
          {result.l1_rows_deleted === 0 && result.l2_rows_deleted === 0 && (
            <div className="text-amber-700 mt-2">
              No rows matched. The cache may have already expired or the query
              text differs from how it was originally cached.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Admin org-impersonation control (Task 7). Self-contained like CacheTab
// above — fetches the active-org list on mount, requires a non-blank
// reason (mirrors the backend's 400-before-lookup ordering in
// admin_impersonation_routes.py), and on a successful
// POST /admin/impersonation/start hands the returned {organization_id,
// name} to ImpersonationContext.start(), which persists it to
// sessionStorage. That sessionStorage entry is what the axios interceptor
// (authApi.ts) AND every raw fetch/SSE call site (impersonationHeaders.ts)
// read on every subsequent request — this panel only ever needs to call
// `start()` once, not thread the org through every hook itself.
const ImpersonateTab: React.FC = () => {
  const { orgId: activeOrgId, orgName: activeOrgName, start } = useImpersonation();
  const [orgs, setOrgs] = useState<{ id: number; name: string }[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    authApiClient.get('/admin/organizations')
      .then(res => {
        if (!cancelled) setOrgs(res.data || []);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load organizations');
      })
      .finally(() => {
        if (!cancelled) setLoadingOrgs(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleStart = async () => {
    if (!selectedOrgId || !reason.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await authApiClient.post('/admin/impersonation/start', {
        organization_id: Number(selectedOrgId),
        reason: reason.trim(),
      });
      start({ orgId: res.data.organization_id, orgName: res.data.name });
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to start impersonation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Impersonate an organization</h3>
        <p className="text-sm text-slate-500 mb-4">
          View the app scoped to another organization's data for support or
          debugging. A reason is required and is written to the audit log
          along with your uid and the target org. Impersonation persists for
          this browser tab only (sessionStorage) — open a new tab, or use
          "Exit" on the banner at the top of every page, to return to your
          own org.
        </p>

        {activeOrgId !== null && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            Currently impersonating <strong>{activeOrgName}</strong>. Use the
            "Exit" button on the banner at the top of the page to stop.
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label htmlFor="impersonate-org" className="block text-sm text-slate-600 mb-1">Organization</label>
            <select
              id="impersonate-org"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              disabled={loadingOrgs || busy}
              className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">{loadingOrgs ? 'Loading organizations...' : 'Select an organization...'}</option>
              {orgs.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="impersonate-reason" className="block text-sm text-slate-600 mb-1">Reason (required)</label>
            <input
              id="impersonate-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. investigating support ticket #1234"
              disabled={busy}
              required
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
            />
          </div>

          <button
            onClick={handleStart}
            disabled={busy || !selectedOrgId || !reason.trim()}
            className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg
                       hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy ? 'Starting...' : 'Start impersonation'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
};

export default AdminPage;
